import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useMappedState } from "redux-react-hook";
import { Button, Card, CardBody } from "@patternfly/react-core";
// it's currently under development and is subject to change.
import { Spinner } from "@patternfly/react-core";
import EditableTable from "./EditableTable";
import NonEditableTable from "./NonEditableTable";
import EditPhaseModal from "./EditPhaseModal";
import Legend from "../general/Legend";
import { REQUESTED_UPDATE_PRODUCT_VERSIONS } from "../../redux/actions/productAction";

export default (props) => {
  const mapState = useCallback(
    (state) => ({
      phaseList: state.phaseState.phaseList,
      productsMap: state.productState.productsMap,
      isUpdatingProductVersions: state.productState.isUpdatingProductVersions,
      isFetchingProductById: state.productState.isFetchingProductById,
      isFetchedProductById: state.productState.isFetchedProductById,
    }),
    []
  );

  const {
    phaseList,
    productsMap,
    isUpdatingProductVersions,
    isFetchingProductById,
    isFetchedProductById,
  } = useMappedState(mapState);
  const dispatch = useDispatch();

  const [product, setProduct] = useState(null);
  const [isEditingVersionPhase, setIsEditingVersionPhase] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productPhases, setProductPhases] = useState(null);

  const buttonStyle = {
    float: "right",
    marginLeft: "1rem",
  };

  const legendStyle = {
    float: "left",
    display: "inline-block",
  };

  useEffect(() => {
    if (props.productId && productsMap) {
      let product = Object.values(productsMap).find(
        (value) => value.id === props.productId
      );
      setProduct(product);
      setProductPhases(
        product.all_phases.map((p) => {
          let phase = phaseList.find((phase) => phase.name === p.name);
          let weight = 0;
          product.versions.forEach((version) => {
            version.phases.forEach((ph) => {
              if (ph.name === phase.name) {
                weight = ph.version_phases.weight;
              }
            });
          });
          return {
            id: phase.id,
            name: phase.name,
            weight,
          };
        })
      );
    }
  }, [props.productId, productsMap]);

  function onCancel() {
    setIsEditingVersionPhase(false);
  }

  const onClickAddVersion = useCallback(() => {
    // add a new empty version object into product
    let newVersion = {
      name: "",
      weight: 0,
    };

    product.versions.unshift(newVersion);
    setIsEditingVersionPhase(true);
  }, [product]);

  const onCancelAddVersion = useCallback(() => {
    if (!product.versions[0].id && product.versions[0].name === "") {
      product.versions.shift();
    }
  }, [product]);

  const onConfirm = useCallback(
    (editVersions) => {
      // do nothing is the editVersions is empty
      if (editVersions.length === 0) {
        return onCancelAddVersion();
      }

      let newProduct = {
        id: props.productId,
        versions: editVersions,
      };

      dispatch({
        type: REQUESTED_UPDATE_PRODUCT_VERSIONS,
        payload: { product: newProduct },
      });
      setIsEditingVersionPhase(false);
    },
    [dispatch, props.productId, onCancelAddVersion]
  );

  function onClickEditPhase() {
    setIsModalOpen(true);
  }

  const handleModalToggle = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const renderButtons = useCallback(() => {
    if (
      !isUpdatingProductVersions &&
      !isFetchingProductById &&
      !isEditingVersionPhase &&
      product
    ) {
      return (
        <Card>
          <CardBody>
            <Legend style={legendStyle} />
            {product.versions && product.versions.length > 0 && (
              <Button
                variant="primary"
                key="add-edit-btn"
                style={buttonStyle}
                onClick={() => {
                  setIsEditingVersionPhase(!isEditingVersionPhase);
                }}
              >
                Edit Version Phase Table
              </Button>
            )}
            {product.versions && product.versions.length > 0 && (
              <Button
                variant="primary"
                key="add-phase-btn"
                style={buttonStyle}
                onClick={() => {
                  onClickEditPhase();
                }}
              >
                Edit Phase
              </Button>
            )}
            <Button
              variant="primary"
              key="add-version-btn"
              style={buttonStyle}
              onClick={() => {
                onClickAddVersion();
              }}
            >
              Add New Version
            </Button>
          </CardBody>
        </Card>
      );
    }
  }, [
    isUpdatingProductVersions,
    isFetchingProductById,
    isEditingVersionPhase,
    buttonStyle,
    onClickAddVersion,
    product,
  ]);

  const renderProductTable = useCallback(() => {
    if (isUpdatingProductVersions || isFetchingProductById || !product) {
      return <Spinner size="xl" />;
    }

    if (product && productPhases) {
      if (isEditingVersionPhase) {
        return (
          <EditableTable
            key={`product-table-editable`}
            product={product}
            onConfirm={onConfirm}
            onCancel={onCancel}
            activeEditId={`version-0-0`}
            productPhases={productPhases}
            onCancelAddVersion={onCancelAddVersion}
          />
        );
      }

      return (
        <NonEditableTable
          key={`product-table-normal`}
          product={product}
          productPhases={productPhases}
        />
      );
    }
  }, [
    isUpdatingProductVersions,
    isFetchingProductById,
    isEditingVersionPhase,
    product,
    productPhases,
    onConfirm,
    onCancelAddVersion,
  ]);

  useEffect(() => {
    if (!isFetchingProductById && isFetchedProductById) {
      setProduct(
        Object.values(productsMap).find((value) => value.id === props.productId)
      );
    }
  }, [
    isFetchingProductById,
    isFetchedProductById,
    productsMap,
    props.productId,
  ]);

  useEffect(() => {
    if (product && productPhases) {
      renderProductTable();
    }
  }, [renderProductTable, product, productPhases]);

  const renderEditPhaseModal = useCallback(() => {
    if (product && props.productId && productPhases && phaseList) {
      return (
        <EditPhaseModal
          product={product}
          productId={props.productId}
          phases={productPhases}
          phaseList={phaseList}
          isModalOpen={isModalOpen}
          handleModalToggle={handleModalToggle}
        />
      );
    }
  }, [
    product,
    props.productId,
    productPhases,
    phaseList,
    handleModalToggle,
    isModalOpen,
  ]);

  useEffect(() => {
    if (product && props.productId && productPhases && phaseList) {
      renderEditPhaseModal();
    }
  }, [
    product,
    props.productId,
    productPhases,
    phaseList,
    renderEditPhaseModal,
  ]);

  return (
    <>
      {renderProductTable()}
      {renderButtons()}
      {renderEditPhaseModal()}
    </>
  );
};
